import {
  Section,
  SectionContent,
  SectionHeading,
  SettingRowDescription,
  SettingRowHeading,
  SettingRowInput,
  SettingsRow,
  SettingsTable,
} from '@/components/Section';
import { Button } from '@/components/ui/button';
import { BooleanConfigInput } from '@/engagements/base/admin/BooleanConfigInput';
import { NumericConfigInput } from '@/engagements/base/admin/NumericConfigInput';
import { QRCodeCtaInput } from '@/engagements/base/admin/QRCodeCtaInput';
import { TitleInput } from '@/engagements/base/admin/TitleInput';
import { Label } from '@/components/ui/label';
import {
  MAX_AUTO_PLAY_INTERVAL_SECONDS,
  MIN_AUTO_PLAY_INTERVAL_SECONDS,
  autoPlayIntervalMsToSeconds,
  secondsToAutoPlayIntervalMs,
} from '@/engagements/slides/consts';
import { dataChanged } from '@/providers/AdminEngagementProvider/actions';
import {
  SlidesAdminConfig,
  SlidesAdminData,
  SlidesSubmissionData,
  useDeleteSubmissionMutation,
  useGetSubmissionsQuery,
} from '@/generated/graphql';
import { useAdminCreateSubmission } from '@/hooks/useAdminCreateSubmission';
import { useAdminEngagementContext } from '@/providers/AdminEngagementProvider/AdminEngagementContext';
import { createLogger } from '@/utils/createLogger';
import {
  createSubmissionTypeFilter,
  SubmissionWithTypename,
} from '@/utils/createSubmissionTypeFilter';
import { PlusIcon } from '@radix-ui/react-icons';
import { useCallback, useMemo } from 'react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { AdminSubmissionProvider } from '@/providers/AdminSubmissionProvider/AdminSubmissionProvider';
import { keyBy } from '@/utils/keyBy';
import { EditableSlideRow } from './EditableSlideRow';

const logger = createLogger('SlidesEditorView');

const isSlidesSubmissionTypeFilter =
  createSubmissionTypeFilter<SlidesSubmissionData>('SlidesSubmissionData');

export function SlidesEditorView() {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    state: { editorMode, ...engagement },
    dispatch,
  } = useAdminEngagementContext();

  const { slidesDisplayOrder, currentSlide } =
    engagement.data as SlidesAdminData;

  const { data: submissionsData, refetch: refetchSubmissions } =
    useGetSubmissionsQuery({
      variables: {
        engagementId: engagement.id,
      },
      fetchPolicy: 'network-only',
    });

  const [deleteSubmission] = useDeleteSubmissionMutation();

  const slides = useMemo(() => {
    logger.info('recomputing slides');
    if (!submissionsData) return [];
    const submissionsById = keyBy(submissionsData?.submissions, 'id');
    return (slidesDisplayOrder || [])
      .map((id) => submissionsById[id])
      .filter(Boolean)
      .filter(isSlidesSubmissionTypeFilter);
  }, [slidesDisplayOrder, submissionsData]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // Get current indexes
      const oldIndex = slidesDisplayOrder.findIndex((s) => s === active.id);
      const newIndex = slidesDisplayOrder.findIndex((s) => s === over.id);

      // Reorder array
      const updatedOrder = [...slidesDisplayOrder];
      const [movedItem] = updatedOrder.splice(oldIndex, 1);
      updatedOrder.splice(newIndex, 0, movedItem);

      dispatch(dataChanged({ data: { slidesDisplayOrder: updatedOrder } }));
    },
    [slidesDisplayOrder, dispatch],
  );

  return (
    <>
      <Section>
        <SectionHeading>Settings</SectionHeading>
        <SectionContent>
          <SettingsTable>
            <SettingsRow>
              <SettingRowHeading>Title</SettingRowHeading>
              <SettingRowDescription>
                Shows as a pulsing title above the QR code. Keep it short.
              </SettingRowDescription>
              <SettingRowInput>
                <TitleInput />
              </SettingRowInput>
            </SettingsRow>
            <SettingsRow>
              <SettingRowHeading>QR code label</SettingRowHeading>
              <SettingRowDescription>
                Displayed above the QR code. Keep it short.
              </SettingRowDescription>
              <SettingRowInput>
                <QRCodeCtaInput />
              </SettingRowInput>
            </SettingsRow>
            <SettingsRow>
              <SettingRowHeading>Auto-play slides</SettingRowHeading>
              <SettingRowDescription>
                Automatically advance to the next slide. Seconds per slide is
                the deck default; a slide with its own duration still overrides
                this.
              </SettingRowDescription>
              <SettingRowInput className="flex flex-wrap items-center gap-4">
                <BooleanConfigInput<SlidesAdminConfig> configKey="autoPlay" />
                <AutoPlayIntervalSecondsInput />
              </SettingRowInput>
            </SettingsRow>
          </SettingsTable>
        </SectionContent>
        {/* <pre>{JSON.stringify(engagement.config, null, 2)}</pre> */}
      </Section>
      <Section>
        <SectionHeading>Slides</SectionHeading>
        <SectionContent>
          <SettingsTable>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={slidesDisplayOrder ?? []}
                strategy={verticalListSortingStrategy}
              >
                {slides.map((submission) => (
                  <AdminSubmissionProvider<SlidesSubmissionData>
                    key={submission.id}
                    submission={submission}
                  >
                    <EditableSlideRow
                      currentSlideId={currentSlide}
                      onSetCurrentSlideId={(id) => {
                        dispatch(
                          dataChanged({
                            data: { currentSlide: id },
                          }),
                        );
                      }}
                      onDelete={(id) => {
                        deleteSubmission({
                          variables: {
                            submissionId: id,
                          },
                        });
                        dispatch(
                          dataChanged({
                            data: {
                              slidesDisplayOrder: slidesDisplayOrder.filter(
                                (s) => s !== id,
                              ),
                            },
                          }),
                        );
                      }}
                    />
                  </AdminSubmissionProvider>
                ))}
              </SortableContext>
            </DndContext>
          </SettingsTable>
          <div className="flex justify-center">
            <AddSlideButton
              className="m-4"
              onSuccess={(submission) => {
                logger.info('AddSlideButton onSuccess', {
                  submission,
                });
                dispatch(
                  dataChanged({
                    data: {
                      slidesDisplayOrder: [
                        ...slidesDisplayOrder,
                        submission.id,
                      ],
                    },
                  }),
                );
                refetchSubmissions();
              }}
            />
          </div>
        </SectionContent>
      </Section>
    </>
  );
}

function AutoPlayIntervalSecondsInput() {
  return (
    <div className="flex items-center gap-2">
      <Label htmlFor="seconds-per-slide" className="whitespace-nowrap text-sm">
        Seconds per slide
      </Label>
      <NumericConfigInput<SlidesAdminConfig>
        id="seconds-per-slide"
        name="seconds-per-slide"
        configKey="autoPlayIntervalMs"
        min={MIN_AUTO_PLAY_INTERVAL_SECONDS}
        max={MAX_AUTO_PLAY_INTERVAL_SECONDS}
        className="max-w-20"
        fromValue={autoPlayIntervalMsToSeconds}
        toValue={secondsToAutoPlayIntervalMs}
      />
    </div>
  );
}

function AddSlideButton({
  className,
  onSuccess,
}: {
  className?: string;
  onSuccess?: (
    submission: SubmissionWithTypename<SlidesSubmissionData>,
  ) => void;
}) {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    state: { editorMode, ...engagement },
  } = useAdminEngagementContext();

  const { createSubmission } = useAdminCreateSubmission<SlidesSubmissionData>();

  const handleAddSlide = () => {
    createSubmission({
      engagementId: engagement.id,
      data: {},
      onSuccess,
    });
  };
  return (
    <Button onClick={handleAddSlide} className={className}>
      <PlusIcon />
      Add Slide
    </Button>
  );
}
